import React, { useEffect, useMemo, useRef, useState } from "react";
import { useInView } from "react-intersection-observer";
import { cn } from "@/lib/utils";

declare global {
  interface Window {
    TradingView: any;
  }
}

export type TvInterval = "1" | "5" | "15" | "30" | "60" | "240" | "D" | "W" | "M";

interface MarketChartProps {
  symbol: string;
  interval?: TvInterval;
  height?: number;
  theme?: "light" | "dark";
  className?: string;
  studies?: string[];
}

const TV_SCRIPT_SRC = "https://s3.tradingview.com/tv.js";

const DEFAULT_STUDIES = ["RSI@tv-basicstudies", "MACD@tv-basicstudies"];

export default function MarketChart({
  symbol,
  interval = "D",
  height,
  theme = "dark",
  className,
  studies,
}: MarketChartProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [scriptLoaded, setScriptLoaded] = useState<boolean>(!!window.TradingView);
  const { ref, inView } = useInView({ triggerOnce: true, rootMargin: "200px" });

  const containerId = useMemo(() => `tv_chart_${Math.random().toString(36).slice(2)}`,[symbol]);

  const activeStudies = studies ?? DEFAULT_STUDIES;

  useEffect(() => {
    if (!inView) return;

    if (!scriptLoaded) {
      const existing = document.querySelector(`script[src='${TV_SCRIPT_SRC}']`);
      if (existing) {
        existing.addEventListener("load", () => setScriptLoaded(true), { once: true });
      } else {
        const script = document.createElement("script");
        script.src = TV_SCRIPT_SRC;
        script.async = true;
        script.onload = () => setScriptLoaded(true);
        document.body.appendChild(script);
      }
    }
  }, [inView, scriptLoaded]);

  useEffect(() => {
    if (!inView || !scriptLoaded || !window.TradingView || !containerRef.current) return;

    containerRef.current.innerHTML = "";

    try {
      new window.TradingView.widget({
        symbol: symbol || "OANDA:EURUSD",
        interval,
        container_id: containerId,
        autosize: true,
        theme,
        style: "1",
        locale: "en",
        withdateranges: true,
        allow_symbol_change: true,
        studies: activeStudies,
        load_last_chart: false,
        hide_side_toolbar: false,
        hide_top_toolbar: false,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
    } catch (e) {
      // fail silently
    }
  }, [inView, scriptLoaded, symbol, interval, theme, containerId, activeStudies]);

  return (
    <div ref={ref} className={cn("w-full", className)}>
      <div
        id={containerId}
        ref={containerRef}
        style={{ height: height ?? "100%", width: "100%" }}
      />
    </div>
  );
}
